import React, {
  FC,
  MouseEvent,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { LOADER_Z_INDEX } from '@components/CustomLoader';
import {
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import { useProjectDashboardActions } from '@store/zustand/project-dashboard-filters';
import { themePalette } from '@theme/muiTheme';
import {
  StyledFilterButton,
  StyledFilterIcon,
} from '@views/Project/components/Filters/Filters.styled';
import {
  FILTERS_POPOVER_OWNER_ATTRIBUTE,
  filtersPopoverOwnerProps,
} from '@views/Project/components/ModelFiltersPopover/filtersPopover.store';
import DashboardPartnersPanel from '@views/Project/components/Toolbox/components/DashboardFilters/DashboardPartnersPanel';
import { useDashboardPartnersPopover } from '@views/Project/components/Toolbox/components/DashboardFilters/dashboardPartnersPopover.store';
import useDashboardPartnerFilter from '@views/Project/components/Toolbox/components/DashboardFilters/useDashboardPartnerFilter';

const DashboardFilters: FC = () => {
  const { options, selected, isAvailable } = useDashboardPartnerFilter();
  const { setPartners, clearPartners } = useProjectDashboardActions();
  const { anchorEl, open, close } = useDashboardPartnersPopover();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const isOpen = Boolean(anchorEl);

  useLayoutEffect(() => {
    if (anchorEl && !anchorEl.isConnected && triggerRef.current) {
      open(triggerRef.current);
    }
  });

  useEffect(() => {
    if (!isAvailable) {
      close();
    }
  }, [isAvailable, close]);

  useEffect(() => close, [close]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      event.stopPropagation();
      close();
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, close]);

  if (!isAvailable) {
    return null;
  }

  const toggle = (event: MouseEvent<HTMLElement>) => {
    if (isOpen) {
      close();

      return;
    }

    open(event.currentTarget);
  };

  const handleClickAway = (event: globalThis.MouseEvent | TouchEvent) => {
    const { target } = event;

    if (
      target instanceof Element &&
      target.closest(`[${FILTERS_POPOVER_OWNER_ATTRIBUTE}]`)
    ) {
      return;
    }

    close();
  };

  const togglePartner = (partner: string) =>
    setPartners(
      selected.includes(partner)
        ? selected.filter((item) => item !== partner)
        : [...selected, partner],
    );

  return (
    <ClickAwayListener
      onClickAway={handleClickAway}
      mouseEvent={isOpen ? 'onPointerDown' : false}
      touchEvent={isOpen ? 'onTouchStart' : false}
    >
      <Box position="relative" display="flex" alignItems="center">
        <StyledFilterButton
          ref={triggerRef}
          $isActive={isOpen}
          $isDisabled={false}
          onClick={toggle}
          aria-label="Filters"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          {...filtersPopoverOwnerProps}
        >
          <StyledFilterIcon fill={themePalette.grey[900]} />

          {selected.length > 0 && (
            <Stack
              data-testid="dashboard-filters-badge"
              alignItems="center"
              justifyContent="center"
              sx={{
                position: 'absolute',
                top: 0,
                right: '-50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: themePalette.primary.main,
                width: 20,
                height: 20,
                borderRadius: '50px',
              }}
            >
              <Typography variant="body2" color="grey.1000">
                {selected.length}
              </Typography>
            </Stack>
          )}
        </StyledFilterButton>

        <Popper
          open={isOpen}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: LOADER_Z_INDEX + 1 }}
        >
          <Paper
            role="dialog"
            aria-label="Outsourcing partner"
            elevation={0}
            sx={{
              width: 340,
              mt: '8px',
              borderRadius: '8px',
              overflow: 'hidden',
              bgcolor: themePalette.white,
              boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.10)',
            }}
          >
            <DashboardPartnersPanel
              options={options}
              selected={selected}
              onToggle={togglePartner}
              onClear={clearPartners}
            />
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default DashboardFilters;
